import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  additionalExpenses,
  type AdditionalExpenseListItem,
} from '@/api/additionalExpenses';
import { contractors as contractorsApi } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABEL: Record<string, string> = {
  TRANSPORT: 'Транспорт',
  INSURANCE: 'Страхування',
  CUSTOMS_BROKER: 'Митний брокер',
  OTHER: 'Інше',
};

export default function AdditionalExpensesPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['additional-expenses'],
    queryFn: () => additionalExpenses.list({ limit: 200 }),
  });
  const ctrQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const ctrName = useMemo(() => {
    const m = new Map<string, string>();
    ctrQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [ctrQ.data]);

  const cols = useMemo<ColumnDef<AdditionalExpenseListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'contractor_id', header: 'Постачальник послуги',
      sortValue: (r) => ctrName.get(r.contractor_id) ?? '',
      cell: (r) => ctrName.get(r.contractor_id) ?? '',
    },
    {
      key: 'expense_type', header: 'Вид', width: 160,
      sortValue: (r) => TYPE_LABEL[r.expense_type] ?? r.expense_type,
      cell: (r) => TYPE_LABEL[r.expense_type] ?? r.expense_type,
    },
    {
      key: 'amount_without_vat', header: 'Без ПДВ', width: 130, align: 'right',
      sortValue: (r) => Number(r.amount_without_vat),
      cell: (r) => fmt2(r.amount_without_vat),
    },
    {
      key: 'vat_amount', header: 'ПДВ', width: 110, align: 'right',
      sortValue: (r) => Number(r.vat_amount),
      cell: (r) => fmt2(r.vat_amount),
    },
    {
      key: 'total_amount', header: 'Усього', width: 130, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt2(r.total_amount),
    },
    {
      key: 'posted', header: 'Проведено', width: 120, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => (r.posted ? '✓ так' : 'ні'),
    },
  ], [ctrName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Додаткові витрати на придбання</h2>
        <button className="primary" onClick={() => navigate('/additional-expenses/new')}>
          + Новий документ
        </button>
      </div>
      <DataTable<AdditionalExpenseListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/additional-expenses/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
